import {
  Box,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { ChartHistory } from '@web-app/components/CallV2/ChartHistory/ChartHistory';
import { GoalsV2 } from '@web-app/components/CallV2/GoalsV2/GoalsV2';
import { JournalEntriesTab } from '@web-app/components/CallV2/JournalEntriesTab/JournalEntriesTab';
import { PatientSubTabs } from '@web-app/components/CallV2/PatientInfoWrapperV2/PatientSubTabs';
import { SessionPrepTab } from '@web-app/components/CallV2/SessionPrepTab';
import { TipTapTab } from '@web-app/components/CallV2/TipTapTab/TipTapTab';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useInSessionClinicalGoals } from '@web-app/utils/feature-flags/in-session-clinical-goals';
import { FC } from 'react';

import { SidePaneTabId } from '../sidePaneTabIds';
import { JournalSidePaneTab } from './JournalSidePaneTab';
import { SidePaneTab } from './SidePaneTab';
import { SidePaneTabErrorBoundary } from './SidePaneTabErrorBoundary';
import { SidePaneTabConfig } from './types';
import { useSidePaneTabs } from './useSidePaneTabs';

interface SidePaneProps {
  appointment?: AppointmentResponse;
  roomId?: string;
}

/**
 * Tabbed interface combining Provider Notes, Clinical Goals, and Patient Information sections
 * for the right-side pane of the CallV2 layout.
 * Allows toggling between Provider Notes, Goals/Interventions, and Patient info.
 */
export const SidePane: FC<SidePaneProps> = ({ appointment, roomId }) => {
  const { visibleTabs, activeIndex, handleTabChange, isTabActive } =
    useSidePaneTabs();
  const { show_warning: showClinicalGoalsWarning } =
    useInSessionClinicalGoals();

  const renderTabContent = (tab: SidePaneTabConfig) => {
    switch (tab.id) {
      case SidePaneTabId.PREP:
        return <SessionPrepTab appointment={appointment} />;
      case SidePaneTabId.NOTES:
        return <TipTapTab appointment={appointment} />;
      case SidePaneTabId.GOALS:
        return (
          <>
            {showClinicalGoalsWarning && (
              <Box p={4} bg="orange.50" borderRadius={16} mb={3}>
                <Text fontSize="sm" color="text.default">
                  Based on your feedback, we're re-evaluating how this tab fits
                  into the in-session experience. We'll share more details
                  before making any changes.
                </Text>
              </Box>
            )}
            <GoalsV2 appointment={appointment} />
          </>
        );
      case SidePaneTabId.PATIENT:
        return (
          <PatientSubTabs
            appointment={appointment}
            isActive={isTabActive(SidePaneTabId.PATIENT)}
            roomId={roomId}
          />
        );
      case SidePaneTabId.CHART_HISTORY:
        return <ChartHistory appointment={appointment} />;
      case SidePaneTabId.JOURNAL:
        return <JournalEntriesTab />;
    }
  };

  return (
    <Box
      h="100%"
      display="flex"
      flexDirection="column"
      data-testid="side-pane"
      px={2}
    >
      <Tabs
        index={activeIndex}
        onChange={handleTabChange}
        size="sm"
        variant="unstyled"
        colorScheme="green"
        h="100%"
        display="flex"
        flexDirection="column"
      >
        <TabList
          borderBottom="2px solid"
          borderColor="border.divider"
          overflowX="auto"
          flexShrink={0}
          data-testid="side-pane-tab-list"
        >
          {visibleTabs.map((tab) =>
            tab.id === SidePaneTabId.JOURNAL ? (
              <JournalSidePaneTab key={tab.id} tab={tab} />
            ) : (
              <SidePaneTab
                key={tab.id}
                label={tab.label}
                data-testid={tab.testId}
              />
            ),
          )}
        </TabList>
        <TabPanels flex="1" overflowY="auto">
          {visibleTabs.map((tab) => (
            <TabPanel key={tab.id} p={tab.noPadding ? 0 : undefined}>
              <SidePaneTabErrorBoundary tabName={tab.errorBoundaryName}>
                {renderTabContent(tab)}
              </SidePaneTabErrorBoundary>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
